import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Divider,
  Chip,
  Avatar,
  InputAdornment,
  makeStyles,
  useMediaQuery,
  Button, // <-- adicionado
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Search } from "@material-ui/icons";
import NewChatModal from "./NewChatModal";

const useStyles = makeStyles((theme) => ({
  sidebar: {
    width: 360,
    maxWidth: "100%",
    borderRight: "1px solid #d0d7de",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#f4f6f8",
    overflow: "hidden",
    boxSizing: "border-box",
    padding: "0 8px",
    paddingBottom: 72, // espaço para botão mobile
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      borderRight: "none",
      padding: "0 6px",
      height: "100%",
      paddingBottom: 84, // mais folga no mobile
    },
  },
  searchBox: {
    background: "#ffffff",
    padding: "14px 16px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    zIndex: 2,
    margin: "8px 0 4px 0",
    borderRadius: 10,
    position: "sticky", // <-- fixo no topo
    top: 0,
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      margin: "6px 0 4px 0",
    },
  },
  listArea: {
    overflowY: "auto",
    flexGrow: 1,
    background: "#f9fafb",
    borderRadius: 8,
    paddingBottom: 4,
    [theme.breakpoints.down("sm")]: {
      paddingBottom: 8,
    },
  },
  listItem: {
    alignItems: "flex-start",
    padding: "10px 12px",
    borderRadius: 10,
    marginBottom: 6,
    transition:
      "background 0.25s ease, box-shadow 0.2s ease, transform 0.15s ease",
    backgroundColor: "#fff",
    position: "relative",
    minHeight: 64, // <-- aumenta touch target
    "&:hover": {
      background: "#e9f5ff",
      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 0,
      background: "#1976d2",
      borderTopLeftRadius: 10,
      borderBottomLeftRadius: 10,
      transition: "width 0.25s ease",
    },
  },
  listItemSelected: {
    backgroundColor: "#d6ecff !important",
    boxShadow: "inset 0 0 0 2px #1976d2",
    "&::before": {
      width: 4,
    },
  },
  avatar: {
    width: 44,
    height: 44,
    marginRight: 12,
    backgroundColor: "#1976d2",
    color: "#fff",
    fontWeight: 600,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: 40,
      height: 40,
      marginRight: 10,
    },
  },
  primaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    color: "#222",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "200px",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "calc(100% - 120px)", // adaptável em mobile
    },
  },
  time: {
    fontSize: 12,
    color: "#999",
    flexShrink: 0,
    marginLeft: 8,
  },
  messagePreview: {
    color: "#555",
    fontSize: 13,
    marginTop: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

export default function ChatList({
  chats = [],
  selectedId,
  onSelect,
  onChatCreated,
  style = {},
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("abertos");
  const [openNew, setOpenNew] = React.useState(false);

  const filtered = chats
    .filter((c) => {
      if (status === "todos") return true;
      if (status === "abertos") return c.status === "aberto";
      if (status === "fechados") return c.status === "finalizado";
      if (status === "automatizados") return c.status === "typebot" || c.status === "selfcheckout";
      return true;
    })
    .filter((c) => {
      const termo = q.toLowerCase();
      return !q
        ? true
        : `${c.nome || ""} ${c.telefone || ""} ${c.ultimoTexto || ""}`
          .toLowerCase()
          .includes(termo);
    });

  return (
    <Box
      className={classes.sidebar}
      style={{
        display: isMobile ? (style.display ?? "block") : style.display,
        width: style.width || (isMobile ? "100%" : 360),
        height: style.height || "100%",
        ...style,
      }}
    >
      {/* 🔍 Busca + filtros */}
      <Box className={classes.searchBox}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar conversa..."
          size="small"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: "#999" }} />
              </InputAdornment>
            ),
            style: { borderRadius: 12 },
          }}
        />

        <Box mt={1.5}>
          {/* 🔹 Linha única de filtros */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            style={{
              gap: 8,
              flexWrap: "nowrap", // 👈 impede quebra
              overflowX: "auto",
              paddingBottom: 4,
            }}
          >
            {[
              { k: "abertos", label: "Abertos" },
              { k: "automatizados", label: "Automático" },
              { k: "fechados", label: "Fechados" },
              { k: "todos", label: "Todos" },
            ].map((b) => (
              <Chip
                key={b.k}
                label={b.label}
                color={status === b.k ? "primary" : "default"}
                size="small"
                onClick={() => setStatus(b.k)}
                style={{
                  fontWeight: status === b.k ? 600 : 400,
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>

          {/* 🔹 Botão "Novo Chat" separado abaixo */}
          <Box mt={1.2} textAlign="center">
            <Chip
              label="Novo Atendimento"
              color="primary"
              size="small"
              onClick={() => setOpenNew(true)}
              style={{
                fontWeight: 700,
                borderRadius: 16,
                cursor: "pointer",
                padding: "2px 10px",
              }}
              clickable
            />
          </Box>
        </Box>

      </Box>

      <Divider />

      {/* 🗂️ Lista de conversas */}
      <Box className={classes.listArea}>
        {filtered.length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography variant="subtitle2">Nenhuma conversa</Typography>
            <Typography variant="body2" color="textSecondary">
              Nenhuma conversa encontrada com esse status ou termo pesquisado.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {filtered.map((chat, index) => {
              const hora =
                chat.ultimaMensagemAt &&
                new Date(chat.ultimaMensagemAt).toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                });

              const key =
                chat.chatId || chat.id || chat.telefone || `item-${index}`;
              const isSelected = selectedId === (chat.chatId || chat.id);

              return (
                <React.Fragment key={key}>
                  <ListItem
                    button
                    onClick={() => onSelect && onSelect({ ...chat })}
                    className={`${classes.listItem} ${isSelected ? classes.listItemSelected : ""
                      }`}
                    style={
                      chat.status === "typebot"
                        ? { borderLeft: "4px solid #ff9800", opacity: 0.9 } // 🟧 destaque automático
                        : {}
                    }
                  >
                    <Avatar className={classes.avatar}>
                      {(chat.nome || chat.telefone || "?")
                        .charAt(0)
                        .toUpperCase()}
                    </Avatar>

                    <ListItemText
                      primary={
                        <div className={classes.primaryRow}>
                          <span className={classes.name}>
                            {chat.nome || chat.telefone}
                          </span>
                          <span className={classes.time}>{hora || ""}</span>
                        </div>
                      }
                      secondary={
                        <Typography noWrap className={classes.messagePreview}>
                          {chat.ultimoTexto || "Sem mensagens"}
                        </Typography>
                      }
                    />
                  </ListItem>

                  {index < filtered.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      <NewChatModal
        open={openNew}
        onClose={() => setOpenNew(false)}
        onCreated={(chat) => {
          onSelect && onSelect(chat);
          setOpenNew(false);
          if (typeof window !== "undefined" && window.onChatCreatedGlobal) {
            window.onChatCreatedGlobal(chat);
          }
        }}
      />
    </Box>
  );
}
